import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import Header from '../../../components/Header';
import api_service from '../../../network/api_callbacks';

export default function FeedbackFormResponses() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const loadResponses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api_service.getFeedbackFormResponses({
        id,
        params: { page, limit: 20 },
      });
      if (response?.responseCode === 200) {
        setForm(response.data?.form || null);
        setRows(response.data?.items || []);
        setPages(response.data?.pagination?.pages || 1);
        setTotal(response.data?.pagination?.total || 0);
      } else {
        toast.error(response?.error?.message || 'Failed to load responses');
        navigate('/feedback-forms');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to load responses');
      navigate('/feedback-forms');
    } finally {
      setLoading(false);
    }
  }, [id, page, navigate]);

  useEffect(() => {
    loadResponses();
  }, [loadResponses]);

  return (
    <Box sx={{ m: '20px', mt: '100px', textAlign: 'left' }}>
      <Header
        title="Form Responses"
        subtitle={form ? `${form.title} · /f/${form.slug}` : 'Loading responses…'}
      />

      <Box
        display="flex"
        flexWrap="wrap"
        gap={1.5}
        alignItems="center"
        mb={3}
        mt={2}
        p={2}
        sx={{
          bgcolor: '#f5f5f5',
          borderRadius: 1,
          border: '1px solid #e0e0e0',
        }}
      >
        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate('/feedback-forms')}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate(`/feedback-forms/${id}`)}
        >
          Edit form
        </Button>
        {form?.slug ? (
          <Button
            variant="contained"
            color="secondary"
            onClick={() => window.open(`${window.location.origin}/f/${form.slug}`, '_blank')}
          >
            Open public form
          </Button>
        ) : null}
      </Box>

      {form ? (
        <Box display="flex" gap={1} mb={2} alignItems="center" flexWrap="wrap">
          <Chip size="small" label={form.status} color={form.status === 'published' ? 'success' : 'default'} />
          <Typography variant="body2" color="text.secondary">
            {total} response{total === 1 ? '' : 's'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Public URL: {`${window.location.origin}/f/${form.slug}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Token URL (form-only): {`${window.location.origin}/f/${form.slug}?token=<USER_ID_TOKEN>`}
          </Typography>
        </Box>
      ) : null}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Submitted</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>User</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No responses yet
                </TableCell>
              </TableRow>
            ) : (
              rows.map((item) => (
                <TableRow key={item._id} hover>
                  <TableCell>
                    {item.created_at ? new Date(item.created_at).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell>{item.email || '-'}</TableCell>
                  <TableCell>{item.name || '-'}</TableCell>
                  <TableCell>{item.userId || 'Guest'}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => navigate(`/feedback-forms/${id}/responses/${item._id}`)}
                    >
                      View detail
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pages > 1 ? (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination page={page} count={pages} onChange={(_, value) => setPage(value)} />
        </Box>
      ) : null}
    </Box>
  );
}
